import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Update as UpdateIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  Link as LinkIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDatabase } from '../../hooks/useDatabase';
import { Contact, Organization } from '../../types/database';
import { PeopleDataLabsPerson } from '../../types/peopledata';

interface PeopleDataLookupProps {
  contact: Contact;
  onContactUpdate?: () => void;
}

export const PeopleDataLookup: React.FC<PeopleDataLookupProps> = ({ contact, onContactUpdate }) => {
  const db = useDatabase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdlData, setPdlData] = useState<PeopleDataLabsPerson | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateFields, setUpdateFields] = useState<Partial<Contact>>({});
  const [organizationName, setOrganizationName] = useState<string>('');
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  
  // Helper to convert null to undefined for Autocomplete
  const getAutocompleteValue = (): Organization | undefined => {
    return selectedOrganization ?? undefined;
  };

  const loadExistingRecord = useCallback(async () => {
    try {
      const lookupValue = contact.email || contact.phone;
      const lookupType = contact.email ? 'email' : 'phone';
      
      if (!lookupValue) return;

      const { data, error: fetchError } = await db.getPeopleRecord(
        contact.id,
        lookupType,
        lookupValue
      );

      if (!fetchError && data) {
        setPdlData(data.pdl_data);
      }
    } catch (err) {
      console.error('Error loading existing record:', err);
    }
  }, [contact.id, contact.email, contact.phone, db]);

  useEffect(() => {
    // Load existing people record if available
    loadExistingRecord();
  }, [loadExistingRecord]);

  const handleLookup = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: lookupError } = await db.lookupPerson(
        contact.id,
        contact.email,
        contact.phone
      );

      console.log('Lookup response:', { data, error: lookupError });

      if (lookupError) {
        setError(lookupError);
        setLoading(false);
        return;
      }

      if (data) {
        console.log('PDL Data received:', {
          hasPerson: !!data.person,
          status: data.status,
          personName: data.person?.full_name || data.person?.first_name || 'N/A'
        });

        // Check if we got actual person data
        if (!data.person) {
          // Show user-friendly "no record" message
          setPdlData(data); // Store the response so we can show the "no record" state
          setLoading(false);
          return;
        }

        setPdlData(data);
        // Reload existing record to get the saved version
        await loadExistingRecord();
      } else {
        setError('No data returned from PeopleData Labs');
      }
    } catch (err) {
      console.error('Error in handleLookup:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUpdateDialog = async () => {
    if (!pdlData?.person) return;

    const person = pdlData.person;
    
    // Load organizations for selection
    const { data: orgsData } = await db.getOrganizations();
    setOrganizations(orgsData || []);
    
    // Load contact's current organizations
    const { data: contactOrgs } = await db.getContactOrganizations(contact.id);
    const currentOrg = contactOrgs && contactOrgs.length > 0 ? contactOrgs[0] : null;
    
    // Prepare update fields from PDL data
    const fields: Partial<Contact> = {};
    
    if (person.full_name && person.full_name !== contact.name) {
      fields.name = person.full_name;
    }
    
    if (person.emails && person.emails.length > 0 && person.emails[0].address && person.emails[0].address.trim() && person.emails[0].address !== contact.email) {
      fields.email = person.emails[0].address;
    }
    
    // Find the first valid phone number (non-empty)
    // Check mobile_phone first, then phone_numbers array
    let phoneNumber = null;
    if (person.mobile_phone && typeof person.mobile_phone === 'string' && person.mobile_phone.trim().length > 0) {
      phoneNumber = person.mobile_phone.trim();
    } else {
      const validPhone = person.phone_numbers?.find(phone => 
        phone?.number && 
        typeof phone.number === 'string' && 
        phone.number.trim().length > 0
      );
      if (validPhone && validPhone.number) {
        phoneNumber = validPhone.number.trim();
      }
    }
    if (phoneNumber && phoneNumber !== (contact.phone || '').trim()) {
      fields.phone = phoneNumber;
    }
    
    // Handle organization from company name or current organization
    let orgName = '';
    let orgToSelect: Organization | null = null;
    
    // If contact already has an organization linked, use that
    if (currentOrg) {
      orgName = currentOrg.name;
      orgToSelect = currentOrg;
    } else {
      // If contact doesn't have an organization, use company name from PeopleData Labs
      if (person.experience && person.experience.length > 0) {
        const currentJob = person.experience[0];
        if (currentJob.company?.name) {
          orgName = currentJob.company.name;
          // Check if organization already exists in the database
          const existingOrg = orgsData?.find(org => 
            org.name.toLowerCase().trim() === currentJob.company?.name?.toLowerCase().trim()
          );
          if (existingOrg) {
            orgToSelect = existingOrg;
          } else {
            // No existing org found, leave as null so user can create new one
            orgToSelect = null;
          }
        }
      }
    }
    
    if (person.experience && person.experience.length > 0) {
      const currentJob = person.experience[0];
      if (currentJob.title && currentJob.title !== contact.title) {
        fields.title = currentJob.title;
      }
    }
    
    setOrganizationName(orgName);
    setSelectedOrganization(orgToSelect);
    
    if (person.skills && person.skills.length > 0) {
      fields.skills = person.skills;
    }
    
    if (person.interests && person.interests.length > 0) {
      fields.interests = person.interests;
    }
    
    // Add social media URLs
    if (person.linkedin_url && person.linkedin_url !== contact.linkedin_url) {
      fields.linkedin_url = person.linkedin_url;
    }
    if (person.twitter_url && person.twitter_url !== contact.twitter_url) {
      fields.twitter_url = person.twitter_url;
    }
    if (person.facebook_url && person.facebook_url !== contact.facebook_url) {
      fields.facebook_url = person.facebook_url;
    }
    if (person.github_url && person.github_url !== contact.github_url) {
      fields.github_url = person.github_url;
    }
    // Note: Instagram is not typically provided by PeopleData Labs API

    setUpdateFields(fields);
    setUpdateDialogOpen(true);
  };

  const handleUpdateContact = async () => {
    setUpdating(true);
    setError(null);

    try {
      // Update contact fields
      const { error: updateError } = await db.updateContact(contact.id, updateFields);

      if (updateError) {
        setError(updateError.message);
        setUpdating(false);
        return;
      }

      // Handle organization linking/unlinking
      const { data: currentOrgs } = await db.getContactOrganizations(contact.id);
      const currentOrgIds = currentOrgs?.map(org => org.id) || [];
      
      if (organizationName.trim()) {
        let orgToLink: Organization | null = selectedOrganization;
        
        // If no organization selected, find or create one
        if (!orgToLink) {
          // Try to find existing organization by name (case-insensitive)
          const existingOrg = organizations.find(org => 
            org.name.toLowerCase().trim() === organizationName.toLowerCase().trim()
          );
          
          if (existingOrg) {
            orgToLink = existingOrg;
          } else {
            // Create new organization
            const { data: newOrg, error: createError } = await db.createOrganization({
              name: organizationName.trim(),
              user_id: contact.user_id,
              description: undefined,
              website: undefined,
              industry: undefined,
              size: undefined,
              location: undefined,
            });
            
            if (createError) {
              console.error('Error creating organization:', createError);
              // Continue even if organization creation fails
            } else if (newOrg) {
              orgToLink = newOrg;
            }
          }
        }
        
        // Link contact to organization if we have one
        if (orgToLink) {
          const isAlreadyLinked = currentOrgIds.includes(orgToLink.id);
          
          if (!isAlreadyLinked) {
            // Unlink all existing organizations first (or keep them if you want multiple)
            // For now, we'll unlink all and link the new one
            for (const orgId of currentOrgIds) {
              await db.unlinkContactFromOrganization(contact.id, orgId);
            }
            
            // Link the new organization
            const { error: linkError } = await db.linkContactToOrganization(contact.id, orgToLink.id);
            if (linkError) {
              console.error('Error linking organization:', linkError);
              // Continue even if linking fails
            }
          }
        }
      } else {
        // If organization name is empty, unlink all organizations
        for (const orgId of currentOrgIds) {
          await db.unlinkContactFromOrganization(contact.id, orgId);
        }
      }

      setUpdateDialogOpen(false);
      setUpdateFields({});
      // Don't reset organizationName and selectedOrganization here - let them persist
      // They will be reset when the dialog is opened again
      
      if (onContactUpdate) {
        onContactUpdate();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setUpdating(false);
    }
  };

  const renderPersonData = () => {
    if (!pdlData) {
      return (
        <Alert severity="info">
          No person data found. Click "Lookup Person" to search PeopleData Labs.
        </Alert>
      );
    }

    if (!pdlData.person) {
      return (
        <Alert severity="info">
          {pdlData.error?.message || 'No person data found in PeopleData Labs database for this email/phone.'}
        </Alert>
      );
    }

    const person = pdlData.person;

    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">PeopleData Labs Results</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<UpdateIcon />}
            onClick={handleOpenUpdateDialog}
          >
            Update Contact
          </Button>
        </Box>

        <Grid container spacing={2}>
          {/* Basic Info */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Basic Information
              </Typography>
              <List dense>
                {person.full_name && (
                  <ListItem>
                    <ListItemText primary="Full Name" secondary={person.full_name} />
                  </ListItem>
                )}
                {person.first_name && (
                  <ListItem>
                    <ListItemText primary="First Name" secondary={person.first_name} />
                  </ListItem>
                )}
                {person.last_name && (
                  <ListItem>
                    <ListItemText primary="Last Name" secondary={person.last_name} />
                  </ListItem>
                )}
                {person.birth_date && (
                  <ListItem>
                    <ListItemText primary="Birth Date" secondary={person.birth_date} />
                  </ListItem>
                )}
                {person.gender && (
                  <ListItem>
                    <ListItemText primary="Gender" secondary={person.gender} />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Contact Information
              </Typography>
              <List dense>
                {person.emails && person.emails.length > 0 && (
                  <>
                    {person.emails
                      .filter(email => email.address && email.address.trim())
                      .map((email, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <EmailIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={email.address} 
                            secondary={email.type || 'Email'} 
                          />
                        </ListItem>
                      ))}
                  </>
                )}
                {/* Display phone numbers from array */}
                {person.phone_numbers && person.phone_numbers.length > 0 && (
                  <>
                    {person.phone_numbers
                      .filter(phone => phone?.number && typeof phone.number === 'string' && phone.number.trim().length > 0)
                      .map((phone, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <PhoneIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={phone.number} 
                            secondary={phone.type || 'Phone'} 
                          />
                        </ListItem>
                      ))}
                  </>
                )}
                {/* Also check mobile_phone directly as fallback (should be in phone_numbers after transformation, but just in case) */}
                {person.mobile_phone && typeof person.mobile_phone === 'string' && person.mobile_phone.trim().length > 0 && 
                 (!person.phone_numbers || !person.phone_numbers.some(phone => phone?.number && phone.number.trim() === person.mobile_phone?.trim())) && (
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={person.mobile_phone.trim()} 
                      secondary="Mobile" 
                    />
                  </ListItem>
                )}
                {person.linkedin_url && (
                  <ListItem>
                    <ListItemIcon>
                      <LinkIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="LinkedIn" 
                      secondary={
                        <a href={person.linkedin_url} target="_blank" rel="noopener noreferrer">
                          {person.linkedin_url}
                        </a>
                      } 
                    />
                  </ListItem>
                )}
                {person.github_url && (
                  <ListItem>
                    <ListItemIcon>
                      <LinkIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="GitHub" 
                      secondary={
                        <a href={person.github_url} target="_blank" rel="noopener noreferrer">
                          {person.github_url}
                        </a>
                      } 
                    />
                  </ListItem>
                )}
                {person.twitter_url && (
                  <ListItem>
                    <ListItemIcon>
                      <LinkIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Twitter" 
                      secondary={
                        <a href={person.twitter_url} target="_blank" rel="noopener noreferrer">
                          {person.twitter_url}
                        </a>
                      } 
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Experience */}
          {person.experience && person.experience.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Experience
                </Typography>
                <List dense>
                  {person.experience.map((exp, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <BusinessIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${exp.title || 'Unknown Title'} at ${exp.company?.name || 'Unknown Company'}`}
                        secondary={
                          <Box>
                            {exp.start_date && exp.end_date && (
                              <Typography variant="caption" display="block">
                                {exp.start_date} - {exp.end_date}
                              </Typography>
                            )}
                            {exp.summary && (
                              <Typography variant="caption" display="block">
                                {exp.summary}
                              </Typography>
                            )}
                            {exp.locations && exp.locations.length > 0 && (
                              <Box display="flex" gap={0.5} mt={0.5}>
                                {exp.locations.map((loc, locIdx) => (
                                  <Chip
                                    key={locIdx}
                                    icon={<LocationIcon />}
                                    label={`${loc.locality || ''} ${loc.region || ''} ${loc.country || ''}`.trim()}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}

          {/* Education */}
          {person.education && person.education.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Education
                </Typography>
                <List dense>
                  {person.education.map((edu, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <SchoolIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={edu.school?.name || 'Unknown School'}
                        secondary={
                          <Box>
                            {edu.degrees && edu.degrees.length > 0 && (
                              <Typography variant="caption" display="block">
                                {edu.degrees.join(', ')}
                              </Typography>
                            )}
                            {edu.start_date && edu.end_date && (
                              <Typography variant="caption" display="block">
                                {edu.start_date} - {edu.end_date}
                              </Typography>
                            )}
                            {edu.summary && (
                              <Typography variant="caption" display="block">
                                {edu.summary}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}

          {/* Skills & Interests */}
          {((person.skills && person.skills.length > 0) || (person.interests && person.interests.length > 0)) && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Skills & Interests
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  {person.skills && person.skills.map((skill, idx) => (
                    <Chip key={idx} label={skill} size="small" />
                  ))}
                  {person.interests && person.interests.map((interest, idx) => (
                    <Chip key={idx} label={interest} size="small" color="secondary" />
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Locations */}
          {person.locations && person.locations.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Locations
                </Typography>
                <List dense>
                  {person.locations.map((loc, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <LocationIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={loc.name || `${loc.locality || ''}, ${loc.region || ''}`.trim()}
                        secondary={`${loc.country || ''} ${loc.continent || ''}`.trim()}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">PeopleData Labs Lookup</Typography>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
          onClick={handleLookup}
          disabled={loading || (!contact.email && !contact.phone && !contact.name)}
        >
          {loading ? 'Looking up...' : 'Lookup Person'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {renderPersonData()}

      {/* Update Contact Dialog */}
      <Dialog 
        open={updateDialogOpen} 
        onClose={() => {
          setUpdateDialogOpen(false);
          setOrganizationName('');
          setSelectedOrganization(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Update Contact with PeopleData Labs Information
          <IconButton
            aria-label="close"
            onClick={() => setUpdateDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            The following fields will be updated in your contact:
          </Alert>
          <Grid container spacing={2}>
            {updateFields.name && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={updateFields.name}
                  onChange={(e) => setUpdateFields({ ...updateFields, name: e.target.value })}
                />
              </Grid>
            )}
            {updateFields.email && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={updateFields.email}
                  onChange={(e) => setUpdateFields({ ...updateFields, email: e.target.value })}
                />
              </Grid>
            )}
            {updateFields.phone && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={updateFields.phone}
                  onChange={(e) => setUpdateFields({ ...updateFields, phone: e.target.value })}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Autocomplete<Organization, false, true, true>
                options={organizations}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name;
                }}
                value={getAutocompleteValue()}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    // If it's a string (freeSolo), clear selection and update name
                    setSelectedOrganization(null);
                    setOrganizationName(newValue);
                  } else {
                    // If it's an Organization or null/undefined
                    setSelectedOrganization(newValue ?? null);
                    if (newValue) {
                      setOrganizationName(newValue.name);
                    } else {
                      setOrganizationName('');
                    }
                  }
                }}
                inputValue={organizationName}
                onInputChange={(event, newInputValue) => {
                  setOrganizationName(newInputValue);
                  // Clear selection if input doesn't match
                  if (selectedOrganization && selectedOrganization.name !== newInputValue) {
                    setSelectedOrganization(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    helperText="Select existing organization or type to create new one"
                  />
                )}
                freeSolo
                clearOnBlur
                selectOnFocus
              />
            </Grid>
            {updateFields.title && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={updateFields.title}
                  onChange={(e) => setUpdateFields({ ...updateFields, title: e.target.value })}
                />
              </Grid>
            )}
            {updateFields.skills && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Skills"
                  value={updateFields.skills.join(', ')}
                  onChange={(e) => setUpdateFields({ 
                    ...updateFields, 
                    skills: e.target.value.split(',').map(s => s.trim()).filter(s => s) 
                  })}
                  helperText="Comma-separated list"
                />
              </Grid>
            )}
            {updateFields.interests && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Interests"
                  value={updateFields.interests.join(', ')}
                  onChange={(e) => setUpdateFields({ 
                    ...updateFields, 
                    interests: e.target.value.split(',').map(s => s.trim()).filter(s => s) 
                  })}
                  helperText="Comma-separated list"
                />
              </Grid>
            )}
            {updateFields.linkedin_url && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="LinkedIn URL"
                  type="url"
                  value={updateFields.linkedin_url}
                  onChange={(e) => setUpdateFields({ ...updateFields, linkedin_url: e.target.value })}
                />
              </Grid>
            )}
            {updateFields.twitter_url && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Twitter/X URL"
                  type="url"
                  value={updateFields.twitter_url}
                  onChange={(e) => setUpdateFields({ ...updateFields, twitter_url: e.target.value })}
                />
              </Grid>
            )}
            {updateFields.facebook_url && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Facebook URL"
                  type="url"
                  value={updateFields.facebook_url}
                  onChange={(e) => setUpdateFields({ ...updateFields, facebook_url: e.target.value })}
                />
              </Grid>
            )}
            {updateFields.github_url && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="GitHub URL"
                  type="url"
                  value={updateFields.github_url}
                  onChange={(e) => setUpdateFields({ ...updateFields, github_url: e.target.value })}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateContact}
            variant="contained"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={20} /> : <UpdateIcon />}
          >
            {updating ? 'Updating...' : 'Update Contact'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

